/**
 * UserMenu 드롭다운 컴포넌트
 * - Radix UI DropdownMenu 기반
 * - 프로필 / 구독 관리 / 초기화 / 로그아웃
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { User, CreditCard, LogOut, ChevronDown, AlertTriangle, HelpCircle, Sun, Moon, Laptop, Settings, Bookmark, Coins, ShieldCheck } from 'lucide-react';
import { resetLocalStorage } from '../../store/store';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useTheme } from '../../hooks/useTheme';
import { creditsApi } from '../../api/credits.api';

interface UserMenuProps {
  user: { name: string; profileImage?: string; role?: 'user' | 'admin' } | null;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    creditsApi.getBalance().then((res) => {
      setCredits(res.data.balance);
    }).catch(() => {
      // silent fail — credits section hidden on error
    });
  }, [user]);

  const cycleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(next);
  };
  const themeIcon = theme === 'light' ? <Sun className="w-4 h-4 shrink-0" aria-hidden="true" /> : theme === 'dark' ? <Moon className="w-4 h-4 shrink-0" aria-hidden="true" /> : <Laptop className="w-4 h-4 shrink-0" aria-hidden="true" />;
  const themeLabel = theme === 'light' ? t('header.userMenu.themeLight') : theme === 'dark' ? t('header.userMenu.themeDark') : t('header.userMenu.themeSystem');

  const handleReset = () => {
    setResetConfirmOpen(true);
  };

  const confirmReset = () => {
    resetLocalStorage();
    window.location.reload();
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  return (
    <>
    <ConfirmDialog
      isOpen={resetConfirmOpen}
      onClose={() => setResetConfirmOpen(false)}
      onConfirm={confirmReset}
      title="설정 초기화"
      description="모든 설정을 초기화하시겠습니까? 이 작업은 되돌릴 수 없습니다."
      confirmText="초기화"
      cancelText="취소"
      variant="destructive"
    />
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          aria-label={t('header.userMenu.profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-white hover:bg-white/20 transition-colors rounded-lg px-3 py-2 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {user?.profileImage ? (
            <img
              src={user.profileImage}
              alt=""
              className="w-6 h-6 rounded-full object-cover shrink-0"
              aria-hidden="true"
            />
          ) : (
            <User className="w-4 h-4 shrink-0" aria-hidden="true" />
          )}
          <span>{user?.name}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[60] min-w-[180px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 animate-in fade-in-0 zoom-in-95"
          sideOffset={6}
          align="end"
        >
          {credits !== null && (
            <DropdownMenu.Item asChild>
              <Link
                to="/credits"
                className="flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 border-b border-gray-100 dark:border-slate-700"
              >
                <span className="flex items-center gap-2.5">
                  <Coins className="w-4 h-4 shrink-0" aria-hidden="true" />
                  크레딧
                </span>
                <span className="text-sm font-semibold text-blue-600">{credits}</span>
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Item asChild>
            <Link
              to="/profile"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <User className="w-4 h-4 shrink-0" aria-hidden="true" />
              {t('header.userMenu.profile')}
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/profile?tab=subscription"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <CreditCard className="w-4 h-4 shrink-0" aria-hidden="true" />
              {t('header.userMenu.subscription')}
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />

          <DropdownMenu.Item asChild>
            <Link
              to="/bookmarks"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <Bookmark className="w-4 h-4 shrink-0" aria-hidden="true" />
              북마크
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/settings"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <Settings className="w-4 h-4 shrink-0" aria-hidden="true" />
              설정
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />

          <DropdownMenu.Item asChild>
            <Link
              to="/pricing"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <CreditCard className="w-4 h-4 shrink-0" aria-hidden="true" />
              요금제
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/help"
              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
            >
              <HelpCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
              {t('header.userMenu.help')}
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />

          <DropdownMenu.Item
            onSelect={(e) => { e.preventDefault(); cycleTheme(); }}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700"
          >
            {themeIcon}
            {t('header.userMenu.theme')}: {themeLabel}
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />

          {user?.role === 'admin' && (
            <>
              <DropdownMenu.Item asChild>
                <Link
                  to="/admin"
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
                >
                  <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden="true" />
                  관리자 대시보드
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />
            </>
          )}

          <DropdownMenu.Item
            onSelect={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer outline-none focus:bg-gray-50 dark:focus:bg-gray-700 focus:text-primary-600 dark:focus:text-primary-400"
          >
            <LogOut className="w-4 h-4 shrink-0" aria-hidden="true" />
            {t('header.userMenu.logout')}
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-gray-700" />

          <DropdownMenu.Item
            onSelect={handleReset}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer outline-none focus:bg-red-50 focus:text-red-700"
          >
            <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
            {t('header.userMenu.reset')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
    </>
  );
};

export default UserMenu;
